/**
 * Invoice PDF Generator
 * Generates a printable invoice document using the browser's print dialog.
 * Used for invoices not sent via Stripe.
 */
import { format, parseISO } from 'date-fns';
import { formatCurrency, getPaymentTermsLabel } from './invoiceData';

/**
 * Generate invoice HTML for printing
 */
export function generateInvoiceHtml(invoice) {
  const invoiceDate = format(parseISO(invoice.invoiceDate), 'MMMM d, yyyy');
  const dueDate = format(parseISO(invoice.dueDate), 'MMMM d, yyyy');
  const isOverdue = invoice.displayStatus === 'overdue' || invoice.status === 'overdue';

  const lineItemRows = invoice.lineItems.map(item => `
    <tr class="${item.type === 'discount' ? 'discount-row' : ''}">
      <td>${item.description}</td>
      <td class="center">${item.type === 'discount' ? '-' : item.quantity}</td>
      <td class="right">${item.type === 'discount' ? '-' : formatCurrency(item.unitPrice)}</td>
      <td class="right ${item.type === 'discount' ? 'discount-amount' : ''}">${formatCurrency(item.total)}</td>
    </tr>
  `).join('');

  const depositSection = invoice.requestDeposit ? `
    <div class="totals-row deposit">
      <span>Deposit Required</span>
      <span>${formatCurrency(invoice.depositAmount)}</span>
    </div>
    ${invoice.depositPaid
      ? `<div class="totals-row deposit-paid"><span>Deposit Paid</span><span>${invoice.depositPaidAt ? format(parseISO(invoice.depositPaidAt), 'MMM d, yyyy') : ''}</span></div>`
      : `<div class="totals-row deposit-due"><span>Deposit Due</span><span>${invoice.depositDueDate ? format(parseISO(invoice.depositDueDate), 'MMM d, yyyy') : '-'}</span></div>`
    }
  ` : '';

  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Invoice ${invoice.invoiceNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      color: #1a1a1a;
      padding: 40px;
      font-size: 13px;
      line-height: 1.6;
      max-width: 800px;
      margin: 0 auto;
    }

    /* Company Header */
    .company-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 24px;
      border-bottom: 2px solid #4a7c59;
      margin-bottom: 28px;
    }
    .company-logo {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .logo-icon {
      font-size: 28px;
    }
    .company-name .name {
      display: block;
      font-size: 20px;
      font-weight: 700;
      color: #1a2634;
    }
    .company-name .tagline {
      display: block;
      font-size: 11px;
      color: #4a7c59;
      text-transform: uppercase;
      letter-spacing: 1.5px;
    }
    .company-contact {
      text-align: right;
      font-size: 12px;
      color: #475569;
      line-height: 1.7;
    }

    /* Bill To & Invoice Info */
    .invoice-parties {
      display: flex;
      justify-content: space-between;
      margin-bottom: 32px;
    }
    .bill-to h3 {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #94a3b8;
      margin-bottom: 6px;
    }
    .bill-to .client-name {
      font-size: 15px;
      font-weight: 600;
      color: #1a2634;
      margin-bottom: 2px;
    }
    .bill-to p {
      font-size: 12px;
      color: #475569;
      line-height: 1.6;
    }
    .invoice-info {
      text-align: right;
    }
    .info-row {
      display: flex;
      justify-content: flex-end;
      gap: 16px;
      margin-bottom: 4px;
    }
    .info-row .label {
      font-size: 12px;
      color: #94a3b8;
    }
    .info-row .value {
      font-size: 12px;
      font-weight: 600;
      color: #1a2634;
      min-width: 130px;
      text-align: right;
    }
    .info-row .value.overdue {
      color: #dc2626;
    }

    /* Line Items Table */
    .line-items-section {
      margin-bottom: 24px;
    }
    .line-items-section h3 {
      font-size: 13px;
      font-weight: 600;
      color: #1a2634;
      margin-bottom: 10px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
    thead tr {
      border-bottom: 2px solid #e2e8f0;
    }
    th {
      padding: 8px 10px;
      text-align: left;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #64748b;
      font-weight: 600;
    }
    th.center { text-align: center; }
    th.right { text-align: right; }
    td {
      padding: 10px;
      font-size: 13px;
      border-bottom: 1px solid #f1f5f9;
    }
    td.center { text-align: center; }
    td.right { text-align: right; }
    .discount-row td { color: #dc2626; }
    .discount-amount { color: #dc2626; }

    /* Totals */
    .invoice-totals {
      margin-left: auto;
      width: 280px;
      border-top: 2px solid #e2e8f0;
      padding-top: 12px;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 6px 0;
      font-size: 13px;
    }
    .totals-row.total {
      border-top: 2px solid #1a2634;
      margin-top: 4px;
      padding-top: 10px;
      font-size: 16px;
      font-weight: 700;
    }
    .totals-row.deposit { color: #b8926a; font-weight: 500; }
    .totals-row.deposit-paid { color: #16a34a; font-size: 12px; }
    .totals-row.deposit-due { color: #b8926a; font-size: 12px; }
    .totals-row.tip { color: #16a34a; }
    .totals-row.paid { color: #16a34a; font-weight: 500; }
    .totals-row.balance {
      border-top: 1px solid #e2e8f0;
      margin-top: 4px;
      padding-top: 8px;
      font-weight: 700;
      color: ${isOverdue ? '#dc2626' : '#1a2634'};
    }

    /* Footer */
    .invoice-footer {
      margin-top: 48px;
      padding-top: 16px;
      border-top: 1px solid #e2e8f0;
      font-size: 11px;
      color: #94a3b8;
      text-align: center;
    }

    @media print {
      body { padding: 20px; }
    }
  </style>
</head>
<body>
  <!-- Company Header -->
  <div class="company-header">
    <div class="company-logo">
      <span class="logo-icon">\uD83C\uDF3F</span>
      <div class="company-name">
        <span class="name">Two Sisters</span>
        <span class="tagline">Cleaning & Organizing</span>
      </div>
    </div>
    <div class="company-contact">
      <p>2114 Angus Rd</p>
      <p>Charlottesville, VA 22901</p>
      <p>(434) 207-2246</p>
    </div>
  </div>

  <!-- Bill To & Invoice Info -->
  <div class="invoice-parties">
    <div class="bill-to">
      <h3>Bill To</h3>
      <p class="client-name">${invoice.clientName}</p>
      ${invoice.propertyAddress ? `<p>${invoice.propertyAddress}</p>` : ''}
      ${invoice.clientEmail ? `<p>${invoice.clientEmail}</p>` : ''}
      ${invoice.clientPhone ? `<p>${invoice.clientPhone}</p>` : ''}
    </div>
    <div class="invoice-info">
      <div class="info-row">
        <span class="label">Invoice Number</span>
        <span class="value">${invoice.invoiceNumber}</span>
      </div>
      <div class="info-row">
        <span class="label">Invoice Date</span>
        <span class="value">${invoiceDate}</span>
      </div>
      <div class="info-row">
        <span class="label">Due Date</span>
        <span class="value ${isOverdue ? 'overdue' : ''}">${dueDate}</span>
      </div>
      <div class="info-row">
        <span class="label">Terms</span>
        <span class="value">${getPaymentTermsLabel(invoice.paymentTerms)}</span>
      </div>
    </div>
  </div>

  <!-- Line Items -->
  <div class="line-items-section">
    <h3>Services</h3>
    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th class="center">Qty</th>
          <th class="right">Unit Price</th>
          <th class="right">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${lineItemRows}
      </tbody>
    </table>
  </div>

  <!-- Totals -->
  <div class="invoice-totals">
    <div class="totals-row">
      <span>Subtotal</span>
      <span>${formatCurrency(invoice.subtotal)}</span>
    </div>
    ${invoice.tax > 0 ? `
    <div class="totals-row">
      <span>Tax</span>
      <span>${formatCurrency(invoice.tax)}</span>
    </div>` : ''}
    <div class="totals-row total">
      <span>Total</span>
      <span>${formatCurrency(invoice.total)}</span>
    </div>
    ${depositSection}
    ${invoice.tipAmount > 0 ? `
    <div class="totals-row tip">
      <span>Tip</span>
      <span>${formatCurrency(invoice.tipAmount)}</span>
    </div>` : ''}
    ${invoice.amountPaid > 0 ? `
    <div class="totals-row paid">
      <span>Amount Paid</span>
      <span>${formatCurrency(invoice.amountPaid)}</span>
    </div>` : ''}
    ${invoice.balance > 0 ? `
    <div class="totals-row balance">
      <span>Balance Due</span>
      <span>${formatCurrency(invoice.balance)}</span>
    </div>` : ''}
  </div>

  <div class="invoice-footer">
    Thank you for your business!
  </div>
</body>
</html>
  `;

  return html;
}

/**
 * Open invoice in a new window for printing/saving as PDF
 */
export function openInvoiceForPrint(invoice) {
  const html = generateInvoiceHtml(invoice);
  const printWindow = window.open('', '_blank', 'width=800,height=1100');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }
  return html;
}
